import React, { useState, useEffect } from "react";
import { Typography, Button, Box, Tabs, Tab, Stack, IconButton, Container } from "@mui/material";
import { Event, FilterList, CalendarMonth, FilterListOff } from "@mui/icons-material/";
import { Dayjs } from "dayjs";
import "dayjs/locale/de";
import { AuthContext } from "../../context/auth-context/AuthContext";
import LoadingTable from "../../components/general/LoadingTable";
import useResponsive from "../../hooks/useResponsive";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { EventPartialDto } from "../../types/eventTypes";

import useEvents from "../../hooks/events/useEvents";
import EventsOverviewFilters from "../../components/events/eventsOverview/EventsOverviewFilters";
import EventsOverviewTable from "../../components/events/eventsOverview/EventsOverviewTable";
import EventsOverviewMobileList from "../../components/events/eventsOverview/EventsOverviewMobileList";
import useWorkshops from "../../hooks/workshops/useWorkshops";
import useMembers from "../../hooks/members/useMembers";
import { MembersFieldDto } from "../../types/membersTypes";
import CreateEventDialog from "../../components/events/eventsOverview/CreateEventDialog";
import WorkingWeekendSignUpDialog from "../../components/events/workingweekend/WorkingWeekendSignUpDialog";
import { applyFiltersAndSortEvents } from "../../utils/EventsUtils";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * Displays the events overview page, all events, all events the user is signed up for and the possibility to sign up or sign out from an event
 * @returns the events overview page
 */
const EventsOverview: React.FC = () => {
  const { auth } = React.useContext(AuthContext);
  const hasEventPermissions = doesPermissionsHaveSomeOf(auth.permissions, [14]);
  const [isNewEventDialogOpen, setIsNewEventDialogOpen] = useState<boolean>(false);

  const [displayedAllEvents, setDisplayedAllEvents] = useState<EventPartialDto[]>([]);
  const [displayedEvents, setDisplayedEvents] = useState<EventPartialDto[]>([]);
  const [displayedWorkshops, setDisplayedWorkshops] = useState<EventPartialDto[]>([]);
  const [displayedEventsSignedUp, setDisplayedEventsSignedUp] = useState<EventPartialDto[]>([]);

  const [displayPastEvents, setDisplayPastEvents] = useState<boolean>(false);

  const [tabValue, setTabValue] = React.useState(0);

  const [displayFiters, setDisplayFilters] = useState<boolean>(false);

  const [startMonthFilter, setStartMonthFilter] = useState<Dayjs | null>(null); // variable that is used for filtering the beginning of the displayed events
  const [endMonthFilter, setEndMonthFilter] = useState<Dayjs | null>(null); // variable that is used for filtering the end of the displayed events
  const [eventTypeFilter, setEventTypeFilter] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState<string>("");

  const [isWWRegistrationDialogOpen, setIsWWRegistrationDialogOpen] = useState<boolean>(false);
  const [workingWeekendToSignUpFor, setWorkingWeekendToSignUpFor] = useState<EventPartialDto | null>(null);

  const {
    allEvents,
    userEvents,
    signUserUpForEvent,
    signUserOutFromEvent,
    createNewEvent,
    signUserUpForWorkingWeekend,
  } = useEvents();
  const { registerForWorkshopInstance, deregisterFromWorkshopInstance } = useWorkshops();
  const { members } = useMembers();

  const membersForOrganizers: MembersFieldDto[] = members.map((member) => ({
    memberId: member.memberId,
    firstname: member.firstname,
    lastname: member.lastname,
    memberStatus: member.memberStatus,
  }));

  const mobile = useResponsive("down", "sm");

  /**
   * Handles the onClose event of the edit dialog
   */
  const handleCloseNewEventDialog = () => {
    setIsNewEventDialogOpen(false);
  };

  /**
   * Handles change of the tab value
   */
  const handleTabChange = (event: React.SyntheticEvent, newTabValue: number) => {
    setTabValue(newTabValue);
  };

  /**
   * Function that renders the tab panel
   */
  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <Box hidden={value !== index} id={`tabpanel-${index}`} {...other}>
        {value === index && <Box sx={{ paddingTop: mobile ? 0 : 3 }}>{children}</Box>}
      </Box>
    );
  };

  // Function to check if the user is signed up for an event, used for displaying the correct button in the event overview table and Mobile List
  const handleCheckIfUserIsSignedUpForEvent = (event: EventPartialDto): boolean => {
    return userEvents.some((e) => e.eventId === event.eventId);
  };

  /**
   * Function to handle the sign up for an event, checks the event type and calls the correct function
   * @param event The event to sign up for
   * @returns void
   */
  const handleSignUpForEvent = async (event: EventPartialDto) => {
    // If the user is already signed up for the event, return
    if (handleCheckIfUserIsSignedUpForEvent(event)) {
      return;
    }

    // If the event is a workshop, call the registerForWorkshopInstance function
    if (event.type === "Workshop" || event.type === "Pflichtworkshop" || event.type === "Externer Workshop") {
      if (!event.workshopId) {
        return;
      } else {
        await registerForWorkshopInstance(event.workshopId, event.eventId);
        return;
      }
    }

    // If the event is a working weekend, open the WW Registration Dialog
    if (event.type === "WW") {
      setWorkingWeekendToSignUpFor(event);
      setIsWWRegistrationDialogOpen(true);
      return;
    }

    if (auth.userID === null) {
      // Theoretically this never happens, only for type safety
      return;
    }

    // For all other events, call the signUserUpForEvent function
    if (event.type === "Netzwerk" || event.type === "JBT goes" || event.type === "Sonstige") {
      await signUserUpForEvent(event.eventId, auth.userID);
    }
  };

  /**
   * Function to handle the sign out from an event, checks the event type and calls the correct function
   * @param event The event to sign out from
   * @returns void
   */
  const handleSignOutFromEvent = async (event: EventPartialDto) => {
    // If the user is not signed up for the event, return
    if (!handleCheckIfUserIsSignedUpForEvent(event)) {
      return;
    }

    // If the event is a workshop, call the deregisterFromWorkshopInstance function
    if (event.type === "Workshop" || event.type === "Pflichtworkshop" || event.type === "Externer Workshop") {
      if (!event.workshopId) {
        return;
      } else {
        await deregisterFromWorkshopInstance(event.workshopId, event.eventId);
        return;
      }
    }

    // For all other events, call the signUserOutFromEvent function
    if (event.type === "WW" || event.type === "Netzwerk" || event.type === "JBT goes" || event.type === "Sonstige") {
      await signUserOutFromEvent(event.eventId, auth.userID ? auth.userID : -1);
      return;
    }
  };

  /**
   * Function that applied the filters to the displayed events and workshops
   */
  const filterEvents = () => {
    // The all events filterd and sorted
    const filteredAllEvents = applyFiltersAndSortEvents(
      allEvents,
      displayPastEvents,
      startMonthFilter,
      endMonthFilter,
      eventTypeFilter,
      nameFilter
    );

    // The user events filtered and sorted
    const filteredUserEvents = applyFiltersAndSortEvents(
      userEvents,
      displayPastEvents,
      startMonthFilter,
      endMonthFilter,
      eventTypeFilter,
      nameFilter
    );

    // The filtered events that are not workshop
    const filteredEvents = filteredAllEvents.filter(
      (event) => event.type !== "Workshop" && event.type !== "Pflichtworkshop" && event.type !== "Externer Workshop"
    );

    // The filtered events that are workshops
    const filteredWorkshops = filteredAllEvents.filter(
      (event) => event.type === "Workshop" || event.type === "Pflichtworkshop" || event.type === "Externer Workshop"
    );
    // Set the filtered and sorted events to the state
    setDisplayedAllEvents(filteredAllEvents);

    // Set the filtered and sorted events that are not workshops to the state
    setDisplayedEvents(filteredEvents);

    // Set the filtered and sorted events that are workshops to the state
    setDisplayedWorkshops(filteredWorkshops);

    // Set the filtered and sorted user events to the state
    setDisplayedEventsSignedUp(filteredUserEvents);
  };

  useEffect(() => {
    filterEvents();
  }, [displayPastEvents, startMonthFilter, endMonthFilter, eventTypeFilter, allEvents, userEvents, nameFilter]);

  /**
   * Returns the content of the actual page
   */
  return (
    <Container maxWidth="lg">
      <WorkingWeekendSignUpDialog
        open={isWWRegistrationDialogOpen}
        handleClose={() => setIsWWRegistrationDialogOpen(false)}
        ww={workingWeekendToSignUpFor}
        onSubmit={signUserUpForWorkingWeekend}
        memberId={auth.userID ? auth.userID : -1}
      />
      <CreateEventDialog
        open={isNewEventDialogOpen}
        onClose={handleCloseNewEventDialog}
        onSubmit={createNewEvent}
        members={membersForOrganizers}
      />
      <Stack direction={mobile ? "column" : "row"} justifyContent={"space-between"}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
          Veranstaltungen
        </Typography>
        <Stack direction={"row"} justifyContent={mobile ? "space-between" : ""}>
          {hasEventPermissions ? (
            <Button
              variant="outlined"
              startIcon={<Event />}
              color="info"
              sx={{ fontWeight: 600, mr: 2 }}
              onClick={() => setIsNewEventDialogOpen(true)}
            >
              Neu
            </Button>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<CalendarMonth />}
            color="primary"
            sx={{ fontWeight: 600 }}
            onClick={() => {
              if (displayPastEvents) {
                setDisplayPastEvents(false);
              } else {
                setDisplayPastEvents(true);
              }
            }}
            size="small"
          >
            {displayPastEvents
              ? mobile
                ? "Aktuelle"
                : "Aktuelle Veranstaltungen"
              : mobile
              ? "Vergangene"
              : "Vergangene Veranstaltungen"}
          </Button>
        </Stack>
      </Stack>
      {allEvents.length > 0 ? (
        <Box>
          <Box>
            {displayFiters ? (
              <EventsOverviewFilters
                displayPastEvents={displayPastEvents}
                eventTypeFilter={eventTypeFilter}
                setEventTypeFilter={setEventTypeFilter}
                setStartMonth={setStartMonthFilter}
                setEndMonth={setEndMonthFilter}
                nameFilter={nameFilter}
                setNameFilter={setNameFilter}
              />
            ) : null}
          </Box>
          <Stack direction={"row"} alignItems="center" justifyContent="space-between">
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={mobile ? "Alle Events" : "Alle Veranstaltungen"} />
              <Tab label={mobile ? "Meine Events" : "Meine Veranstaltungen"} />
              {!mobile ? <Tab label="Events" /> : null}
              {!mobile ? <Tab label="Workshops" /> : null}
            </Tabs>
            <IconButton
              sx={{ width: 35, height: 35 }}
              onClick={() => {
                setDisplayFilters((prev) => !prev);
              }}
            >
              {displayFiters ? <FilterListOff /> : <FilterList />}
            </IconButton>
          </Stack>
          <CustomTabPanel value={tabValue} index={0}>
            {mobile ? (
              <EventsOverviewMobileList
                events={displayedAllEvents}
                checkIfUserIsSignedUpForEvent={handleCheckIfUserIsSignedUpForEvent}
                onSignUp={handleSignUpForEvent}
                onSignOut={handleSignOutFromEvent}
              />
            ) : (
              <EventsOverviewTable
                events={displayedAllEvents}
                checkIfUserIsSignedUpForEvent={handleCheckIfUserIsSignedUpForEvent}
                onSignUp={handleSignUpForEvent}
                onSignOut={handleSignOutFromEvent}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={1}>
            {mobile ? (
              <EventsOverviewMobileList
                events={displayedEventsSignedUp}
                checkIfUserIsSignedUpForEvent={handleCheckIfUserIsSignedUpForEvent}
                onSignUp={handleSignUpForEvent}
                onSignOut={handleSignOutFromEvent}
              />
            ) : (
              <EventsOverviewTable
                events={displayedEventsSignedUp}
                checkIfUserIsSignedUpForEvent={handleCheckIfUserIsSignedUpForEvent}
                onSignUp={handleSignUpForEvent}
                onSignOut={handleSignOutFromEvent}
              />
            )}
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={2}>
            <EventsOverviewTable
              events={displayedEvents}
              checkIfUserIsSignedUpForEvent={handleCheckIfUserIsSignedUpForEvent}
              onSignUp={handleSignUpForEvent}
              onSignOut={handleSignOutFromEvent}
            />
          </CustomTabPanel>
          <CustomTabPanel value={tabValue} index={3}>
            <EventsOverviewTable
              events={displayedWorkshops}
              checkIfUserIsSignedUpForEvent={handleCheckIfUserIsSignedUpForEvent}
              onSignUp={handleSignUpForEvent}
              onSignOut={handleSignOutFromEvent}
            />
          </CustomTabPanel>
        </Box>
      ) : (
        <LoadingTable />
      )}
    </Container>
  );
};

export default EventsOverview;
